import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth, firestore } from './firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SuperAdminProvider, useSuperAdmin } from './contexts/SuperAdminContext';
import { Button } from '@mui/material';
import Loader from './components/UI/Feedback/Loader.js';
import SuperAdminTopBar from './superAdmin/SuperAdminTopBar.js';
import WorkspaceNotFound from './components/UI/Errors/WorkspaceNotFound.js';
import SuperAdminLogin from './superAdmin/SuperAdminLogin';
import './styles/global.css';
import ErrorBoundary from './components/ErrorBoundary';

// ─── Lazy-loaded routes ────────────────────────────────────────────────────────
const WebConfiguratorNew = React.lazy(() => import('./features/webConfiguratorNew/WebConfigurator.js'));
const WebConfigurator    = React.lazy(() => import('./features/webConfigurator/WebConfigurator.js'));
const Dashboard          = React.lazy(() => import('./components/DashboardRouter.js'));
const LandingPage        = React.lazy(() => import('./pages/LandingPage'));
const SuperAdminDashboard = React.lazy(() => import('./superAdmin/SuperAdminDashboard'));
const Login              = React.lazy(() => import('./components/userAuthUI/Login.js'));
const Register           = React.lazy(() => import('./components/userAuthUI/Register.js'));
const KingswardLeadgen = React.lazy(() => import('./features/leadgen/kingsward'));

// ─── SuperAdmin route ─────────────────────────────────────────────────────────
const SuperAdminRoute = () => {
  const { user, loading, realClaims } = useAuth();
  const navigate = useNavigate();

  if (loading) return <Loader />;
  if (!user)   return <Navigate to="/admin/login" replace />;

  if (!realClaims?.isSuperAdmin) {
    return (
      <div style={{ color: 'red', margin: 40, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>SuperAdmin privileges required.</p>
        <Button variant="contained" onClick={() => navigate('/admin/login')}>
          Go to Login
        </Button>
      </div>
    );
  }

  return <SuperAdminDashboard />;
};
const RedirectToLogin = ({ workspaceId }) => {
  const location = useLocation();
  return (
    <Navigate
      to={`/${workspaceId}/login`}
      replace
      state={{ from: location.pathname + location.search }}
    />
  );
};
// ─── Workspace routes ─────────────────────────────────────────────────────────
const WorkspaceRoutes = () => {
  const { workspaceId }                       = useParams();
  const { user, loading, claims, realClaims } = useAuth();
  const { isImpersonating }                   = useSuperAdmin();
  const [workspaceData,    setWorkspaceData]    = useState(null);
  const [workspaceChecked, setWorkspaceChecked] = useState(false);
  const [error,            setError]            = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!workspaceData) return;
    document.title = workspaceData.displayName;
    const appleTitle = document.querySelector("meta[name='apple-mobile-web-app-title']");
    if (appleTitle) appleTitle.content = workspaceData.displayName;
    if (workspaceData.iconUrl) {
      const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
      if (appleIcon) appleIcon.href = workspaceData.iconUrl;
    }
  }, [workspaceData]);

  useEffect(() => {
    const checkWorkspace = async () => {
      try {
        const wsDoc = await getDoc(doc(firestore, 'Workspaces', workspaceId));
        if (!wsDoc.exists()) {
          setError('Workspace does not exist.');
        } else {
          setWorkspaceData({
            id:          workspaceId,
            headOrgId:   wsDoc.data().headOrgId,
            iconUrl:     wsDoc.data().iconUrl     || '',
            logoUrl:     wsDoc.data().logoUrl     || '',
            name:        wsDoc.data().name        || workspaceId,
            displayName: wsDoc.data().displayName || wsDoc.data().name || workspaceId,
          });
        }
      } catch (err) {
        console.error('Error loading workspace:', err);
        setError('Failed to load workspace.');
      } finally {
        setWorkspaceChecked(true);
      }
    };
    checkWorkspace();
  }, [workspaceId]);

  if (!workspaceChecked) return <Loader />;
  if (!workspaceData)    return <WorkspaceNotFound error={error} />;
  if (loading)           return <Loader   logo= "/images/cp-logo.svg"
/>;

  if (!user) {
    return (
      <Routes>
        <Route path="login"    element={<Login    workspaceId={workspaceId} headOrgId={workspaceData.headOrgId} logoUrl={workspaceData.logoUrl} />} />
        <Route path="register" element={<Register workspaceId={workspaceId} />} />
<Route path="*" element={<RedirectToLogin workspaceId={workspaceId} />} />
      </Routes>
    );
  }

  const isSuperAdmin    = realClaims?.isSuperAdmin === true;
  const effectiveClaims = claims || {};

  if (!isSuperAdmin && effectiveClaims.headOrgId !== workspaceData.headOrgId) {
    console.error('❌ headOrgId mismatch', effectiveClaims.headOrgId, '≠', workspaceData.headOrgId);
    return (
      <div style={{ color: 'red', margin: 40, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You do not have access to this workspace.</p>
        <button onClick={async () => { await signOut(auth); navigate('https://flowcpq.com/'); }}>Sign Out</button>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="*" element={
            <ErrorBoundary
      orgId={effectiveClaims.orgId || effectiveClaims.headOrgId}
      userId={effectiveClaims.uid || user.uid}
      userName={user.displayName || user.email}
    >
        <Dashboard
          user={user}
          workspaceId={workspaceData.id}
          userId={effectiveClaims.uid  || user.uid}
          orgId={effectiveClaims.orgId || effectiveClaims.headOrgId}
          headOrgId={effectiveClaims.headOrgId}
          claims={effectiveClaims}
          workspaceData={workspaceData}
        /></ErrorBoundary>
      } />
    </Routes>
  );
};

// ─── App ───────────────────────────────────────────────────────────────────────
const App = () => (
  <AuthProvider>
    <SuperAdminProvider>
      <SuperAdminTopBar />
      <Suspense fallback={<Loader/>}>
        <Routes>
          <Route path="/"                           element={<LandingPage />} />
          <Route path="/cubepools/web-configurator-old" element={<WebConfigurator />} />
          <Route path="/cubepools/web-configurator" element={<WebConfiguratorNew />} />
          <Route path="/leadgen/step" element={<KingswardLeadgen headOrgId="YOUR_KINGSWARD_ORG_ID" />} />
          <Route path="/admin/login"                element={<SuperAdminLogin />} />
          <Route path="/admin/*"                    element={<SuperAdminRoute />} />
          <Route path="/:workspaceId/*"             element={<WorkspaceRoutes />} />
        </Routes>
      </Suspense>
    </SuperAdminProvider>
  </AuthProvider>
);

export default App;